import { observer } from 'mobx-react-lite';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { BaseTextField, Checkbox, HelperText, TableCell, TableRow } from '@ifs-gcp/components';
import type { IScopeNode, IScopesNode, ProductScopes, ValidationError } from '@ifs-gcp/core';
import type { ProductScopeChild, ProductScopeWidgetChild } from '@ifs-gcp/core/models';

import { useDebouncedInput } from '@/hooks/useDebouncedInput';
import type { useTouchedField } from '@/store/validationStore';
import { getTranslation } from '@/utils/translations';

export interface ScopeProps {
  domain: ProductScopes;
  template: IScopeNode<ProductScopeChild>;
  errors?: ValidationError[];
  element: IScopesNode<ProductScopeWidgetChild>;
  markTouched: ReturnType<typeof useTouchedField>['markTouched'];
}

export const ScopeRow = observer(
  ({ errors, domain, template, element, markTouched }: ScopeProps) => {
    const {
      i18n: { language },
    } = useTranslation();

    const scope = domain.getScopeByUuid(template.uuid);

    const {
      props: { id: widgetId },
    } = element;

    const { inputValue, handleChange } = useDebouncedInput({
      initialValue: scope.textFieldContent,
      onChange: (value) => {
        domain.addDescription(template.uuid, value);

        markTouched();
      },
    });

    const handleSaveTextAnswer = (
      event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      const trimmedValue = event.target.value.trim();
      domain.addDescription(template.uuid, trimmedValue);
      markTouched();
    };

    const handleChangeScope = () => {
      domain.changeScope(template.uuid);

      markTouched();
    };

    const scopeRowError = errors?.find(
      ({ id: errorId }) => `${widgetId}_${scope.template.uuid}` === errorId,
    );

    return (
      <TableCell>
        <Checkbox
          onChange={handleChangeScope}
          checked={scope.selected}
          disabled={scope.disabled}
          label={getTranslation(scope.template.text, language)}
          data-testid={`product-scope-${scope.template.uuid}`}
        />

        {scope.hasTextField && (
          <BaseTextField
            value={inputValue}
            disabled={scope.disabled}
            onChange={handleChange}
            onBlur={handleSaveTextAnswer}
          />
        )}

        {scopeRowError && (
          <HelperText error component="span">
            {getTranslation(scopeRowError.error, language)}
          </HelperText>
        )}

        {Boolean(template.children.length) && (
          <table>
            <tbody>
              {template.children.map((subScope) => (
                <TableRow key={subScope.uuid}>
                  <ScopeRow
                    errors={errors}
                    template={subScope}
                    element={element}
                    domain={domain}
                    markTouched={markTouched}
                  />
                </TableRow>
              ))}
            </tbody>
          </table>
        )}
      </TableCell>
    );
  },
);
